import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Mail, ArrowLeft, AlertTriangle } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type FormData = z.infer<typeof formSchema>;

function WarmAlert({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 rounded-lg px-3 py-2 mt-2 shadow-sm text-sm">
      <AlertTriangle className="w-4 h-4 text-orange-400" />
      <span>{message}</span>
    </div>
  );
}

const ForgotPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = (data: FormData) => {
    setIsLoading(true);
    
    // Simulate API call for password reset
    setTimeout(() => {
      setIsLoading(false);
      
      toast({
        title: "Reset link sent",
        description: `If ${data.email} exists in our system, you will receive password reset instructions.`,
      });
      
      // Redirect back to login page after successful submission
      navigate("/login");
    }, 1000);
  };

  return (
    <div className="bg-sauna-beige min-h-screen flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-2xl border-none rounded-3xl p-0 sm:p-2 bg-white/90">
        <CardHeader className="flex flex-col items-center pt-2 pb-6">
          <div className="rounded-full bg-gradient-to-b from-sauna-orange/80 to-sauna-orange p-5 mb-6 shadow-lg">
            <Mail className="h-12 w-12 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-sauna-navy mb-1 text-center">Reset Password</CardTitle>
          <CardDescription className="text-lg text-sauna-navy/80 mb-4 text-center font-medium">Enter your email to receive a password reset link</CardDescription>
        </CardHeader>
        <CardContent className="px-6 pb-8 pt-0">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-7" noValidate>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sauna-navy font-semibold mb-1 block">Email</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input
                          id="reset-email"
                          placeholder="your@email.com"
                          className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-lg py-3 rounded-xl"
                          {...field}
                        />
                      </FormControl>
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-sauna-orange" />
                      {form.formState.errors.email && <WarmAlert message={form.formState.errors.email.message} />}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-sauna-orange to-sauna-orange/80 text-white text-xl py-3 mt-2 rounded-xl shadow-md hover:from-sauna-orange/90 hover:to-sauna-orange transition font-bold tracking-wide disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          </Form>
          <div className="text-center mt-6 text-sauna-navy/80 text-base">
            <Button asChild variant="ghost" className="text-sm">
              <Link to="/login">
                <ArrowLeft size={16} className="mr-1" />
                Back to Login
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordForm;
